import "./SendoutsFilterComponent.scss";
import React, {useCallback, useEffect, useRef, useState} from "react";
import {useSelector} from "react-redux";
import {IRootReducerState} from "../../../store/reducers";
import CommonService from "../../../shared/services/common.service";
import {Field, FieldProps, Form, Formik, FormikProps, FormikValues} from "formik";
import FormikAutoCompleteDropdown
    from "../../../shared/components/core/formik-auto-complete-dropdown/formikAutoCompleteDropdown";
import FormikDatePickerComponent from "../../../shared/components/core/formik-date-picker/FormikDatePickerComponent";
import ButtonComponent from "../../../shared/components/core/button/ButtonComponent";
import HeadingComponent from "../../../shared/components/heading/headingComponent";
import formValidationRules from "../../../shared/services/validation.service";
import * as Yup from "yup";
import useFetch from "../../../shared/hooks/useFetch";
import {APIConfig} from "../../../constants";
import {LabRoleType} from "../../../shared/models/lab.model";

interface SendoutsFilterComponentProps {
    filtersData: any;
    onFiltersApply: (filterData: any) => void;
    clearAllFilters: () => void;
}

const validationSchema = Yup.object().shape({
    startDate: Yup.mixed().nullable().when("endDate", {
        is: (endDate: Date) => {
            return Boolean(endDate)
        },
        then: Yup.mixed().nullable().required("Start date is required"),
        otherwise: Yup.mixed().nullable().notRequired()
    }),
    endDate: Yup.mixed().nullable().when("startDate", {
        is: (startDate: Date) => {
            return Boolean(startDate)
        },
        then: Yup.mixed().nullable().required("End date is required"),
        otherwise: Yup.mixed().nullable().notRequired()
    })
}, [['startDate', 'endDate']]);

const SendoutsFilterComponent = (props: SendoutsFilterComponentProps) => {
    const {filtersData, onFiltersApply, clearAllFilters} = props;
    const [dataFiltersInitialState, setDataFiltersInitialState] = useState(filtersData);
    const {userList} = useSelector((state: IRootReducerState) => state.staticData);
    const formikRef = useRef<FormikProps<FormikValues>>(null);

    const {
        response: labList = [],
        loading: isLabListLoading,
        loadingFailed: isLabListLoadingFailed,
        fetchData: fetchLabList
    } = useFetch<any[]>();

    const onFiltersApplyHandler = useCallback((values: any) => {
        const payload = {
            ...values
        }
        payload.createdByIds = payload.createdByObjs.map((item: any) => item.id);
        payload.labIds = payload.labObjs.map((item: any) => item.id);
        payload.startDate = payload.startDate ? CommonService.convertDateFormat(values['startDate'], 'YYYY-MM-DD') : null;
        payload.endDate = payload.endDate ? CommonService.convertDateFormat(values['endDate'], 'YYYY-MM-DD') : null;

        onFiltersApply(payload);
    }, [onFiltersApply]);

    const getLabList = useCallback(() => {
        fetchLabList({
            url: APIConfig.FILTERED_LAB_LIST_LITE.URL,
            method: APIConfig.FILTERED_LAB_LIST_LITE.METHOD,
            payload: {"isActive": true, labRoles: ["sendLab", "sendReceiveLab"] as LabRoleType[],labType:'externalLab'}
        })
    }, [fetchLabList]);

    useEffect(() => {
        setDataFiltersInitialState(filtersData);
    }, [filtersData]);

    useEffect(() => {
        getLabList();
    }, [getLabList]);

    return (
        <div className={'sendouts-filter-component screen t-form-screen'}>
            <>
                <div className="drawer-sub-header horizontal-vertical-center-align">
                    <HeadingComponent title={"Filters"}/>
                    <div className={'filters-clear-all-button'}
                         onClick={() => {
                             formikRef.current && formikRef.current.resetForm()
                             clearAllFilters()
                         }}
                    >
                        Clear all
                    </div>
                </div>
                <div className="t-form t-form-drawer">
                    <Formik
                        initialValues={dataFiltersInitialState}
                        enableReinitialize={true}
                        onSubmit={onFiltersApplyHandler}
                        innerRef={formikRef}
                        validationSchema={validationSchema}
                    >
                        {({isSubmitting, values, isValid, setFieldValue, validateForm, errors, dirty}) => {
                            // eslint-disable-next-line react-hooks/rules-of-hooks
                            useEffect(() => {
                                validateForm();
                            }, [values, validateForm]);
                            return (
                                <Form noValidate={true} className={"patient-filters-holder"}>
                                    <div className="t-form-controls-wrapper">

                                        <Field name={'labObjs'}>
                                            {
                                                (field: FieldProps) => (
                                                    <FormikAutoCompleteDropdown
                                                        fullWidth={true}
                                                        label={'Lab Name'}
                                                        multiple={true}
                                                        data={labList || []}
                                                        isDataLoadingFailed={Boolean(isLabListLoadingFailed)}
                                                        isDataLoading={isLabListLoading}
                                                        valueKey={"id"}
                                                        placeholder={'Select Lab Name'}
                                                        formikField={field}
                                                        getOptionLabel={(option => (option.name) || '')}
                                                    />
                                                )
                                            }
                                        </Field>

                                        <Field name={'createdByObjs'}>
                                            {
                                                (field: FieldProps) => (
                                                    <FormikAutoCompleteDropdown
                                                        fullWidth={true}
                                                        label={'Added by'}
                                                        multiple={true}
                                                        data={userList}
                                                        valueKey={"id"}
                                                        placeholder={'Select Added by'}
                                                        formikField={field}
                                                        getOptionLabel={(option => (option.firstName + ' ' + option.lastName) || '')}
                                                    />
                                                )
                                            }
                                        </Field>
                                        <Field name={'startDate'}>
                                            {
                                                (field: FieldProps) => (
                                                    <FormikDatePickerComponent
                                                        label={'Start Date'}
                                                        formikField={field}
                                                        placeholder={'Select End Date'}
                                                        maxDate={formValidationRules.today}
                                                    />
                                                )
                                            }
                                        </Field>
                                        <Field name={'endDate'}>
                                            {
                                                (field: FieldProps) => (
                                                    <FormikDatePickerComponent
                                                        label={'End Date'}
                                                        formikField={field}
                                                        placeholder={'Select End Date'}
                                                        maxDate={formValidationRules.today}
                                                    />
                                                )
                                            }
                                        </Field>
                                    </div>
                                    <div className="drawer-actions form-action-section">
                                        <ButtonComponent variant={"contained"}
                                                         fullWidth={true}
                                                         color={"primary"}
                                                         type={"submit"}
                                                         disabled={!isValid || !dirty}
                                        >
                                            Apply
                                        </ButtonComponent>
                                    </div>
                                </Form>
                            )
                        }
                        }
                    </Formik>
                </div>
            </>
        </div>
    );

};

export default SendoutsFilterComponent;
